import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import {
    calculateBeneiftsCharge,
    calculateBeneiftsNet,
    getCrystallisationAmount,
    showBenefitLastTwoColumn,
    showBenefitTable,
} from "./Helper/DrawdownDetailHelper";

interface ITaxFreeLumpSumPaymentProps extends IProcessComponentProps {
    data: DrawdownModel;
    pensionProtection: PensionProtectionDetails;
    process: ProcessType;
    lessThan75: boolean;
    hasEnhancedPensionProtection: boolean;
}

interface ITaxFreeLumpSumPaymentState {
    isConfirm: boolean;
}

export class TaxFreeLumpSumPayment extends ProcessComponent<ITaxFreeLumpSumPaymentProps, ITaxFreeLumpSumPaymentState> {
    constructor(props: any) {
        super(props);
        this.state = {
            isConfirm: false,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        let lumpsumCharge = null;
        let lumpsumNet = null;
        let incomeCharge = null;
        let incomeNet = null;
        if (this.props.data?.GrossExcessBenefitLumpSum && this.props.data?.GrossExcessBenefitLumpSum > 0) {
            lumpsumCharge = calculateBeneiftsCharge(
                this.props.data,
                this.props.data.GrossExcessBenefitLumpSum,
                "lumpsum"
            );
            lumpsumNet = calculateBeneiftsNet(this.props.data, this.props.data.GrossExcessBenefitLumpSum, "lumpsum");
        }
        if (
            this.props.data?.GrossExcessBenefitProvideAdditionalIncome &&
            this.props.data?.GrossExcessBenefitProvideAdditionalIncome > 0
        ) {
            incomeCharge = calculateBeneiftsCharge(
                this.props.data,
                this.props.data.GrossExcessBenefitProvideAdditionalIncome,
                "income"
            );
            incomeNet = calculateBeneiftsNet(
                this.props.data,
                this.props.data.GrossExcessBenefitProvideAdditionalIncome,
                "income"
            );
        }
        this.onDataChange(
            this.props.onChange,
            {
                ExcessBenefitsCalculations: {
                    LumpSumTaxPayable: { Amount: lumpsumCharge, Currency: CurrencyCode.GBP },
                    LumpSumTotalNetBenefits: { Amount: lumpsumNet, Currency: CurrencyCode.GBP },
                    AdditionalIncomeTaxPayable: { Amount: incomeCharge, Currency: CurrencyCode.GBP },
                    AdditionalIncomeTotalNetBenefits: { Amount: incomeNet, Currency: CurrencyCode.GBP },
                },
            },
            this.props.data
        );
    }

    isValid() {
        const { data, pensionProtection, showErrorMsgPopUp, lessThan75, hasEnhancedPensionProtection } = this.props;
        const isPrimary = pensionProtection?.PensionPrimaryProtectionDetails;
        const crystallisationAmount = data?.CrystallisationDetails?.CrystallisationAmount?.Amount;
        if (isPrimary && data?.CurrentPclsEntitlement?.Amount == crystallisationAmount) {return false;}
        if (showBenefitTable(data, lessThan75, hasEnhancedPensionProtection) && data?.GrossExcessBenefitTotal !== 100) {
            showErrorMsgPopUp(["Please enter values for the gross excess benefits that total 100%."]);
            return false;
        }
        return super.isValid();
    }

    public render(): JSX.Element {
        const { data, onChange, pensionProtection, process, lessThan75, hasEnhancedPensionProtection } = this.props;

        const isPrimary = pensionProtection?.PensionPrimaryProtectionDetails;
        const isPrimaryProtectedTaxFreeCashEntitlement =
            pensionProtection?.PensionPrimaryProtectionDetails?.PclsEntitlement &&
            pensionProtection?.PensionPrimaryProtectionDetails?.PclsEntitlement > 0;
        const crystallisationAmount = getCrystallisationAmount(data);

        return (
            <>
                {process == ProcessType.TakePensionBenefits && data?.PensionOption == PensionOption.UFPLS ? (
                    <div className="row">
                        <div className="col-12 col-xs-6">
                            <div className="panel-blue">
                                <h3 className="mb-5">Tax-free element of uncrystallised funds pension lump sum:</h3>
                                <span>{MoneyDisplayHelper.getMoneyFormat(data?.UfplsWithdrawal?.TaxFreePortion)}</span>
                                <hr />
                                <h3 className="mb-5">Taxable element of uncrystallised funds pension lump sum:</h3>
                                <span>{MoneyDisplayHelper.getMoneyFormat(data?.UfplsWithdrawal?.TaxablePortion)}</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-12 col-xs-6">
                            <div className="panel-blue pb-5">
                                <h3 className="mb-5">Tax free lump sum payment:</h3>
                                {isPrimary ? (
                                    <>
                                        <MoneyTextBoxEditor
                                            cssWrapper="control-size-245"
                                            currency={CurrencyCode.GBP}
                                            value={data?.TaxFreeLumpSum?.Amount}
                                            required={"Please enter"}
                                            onChange={(v) =>
                                                onChange({
                                                    TaxFreeLumpSum: { Amount: v, Currency: CurrencyCode.GBP },
                                                })
                                            }
                                            validationRules={[
                                                {
                                                    errorMessage: "Please enter an amount that is greater than zero.",
                                                    validator: (v) => v > 0,
                                                },
                                                {
                                                    errorMessage:
                                                        "Please specify a value for the TFLS to be paid that is less than the crystallisation amount and less than or equal to the maximum TFLS available.",
                                                    validator: (v) =>
                                                        v <= data?.CrystallisationDetails?.CrystallisedValue?.Amount ||
                                                        v <= data?.CurrentPclsEntitlement?.Amount,
                                                },
                                            ]}
                                            infoText={`Maximum PCLS available: ${MoneyDisplayHelper.getMoneyFormat(
                                                data?.CurrentPclsEntitlement
                                            )}`}
                                            disabled={!isPrimaryProtectedTaxFreeCashEntitlement}
                                        />
                                        {data?.TaxFreeLumpSum?.Amount < data?.CurrentPclsEntitlement?.Amount && (
                                            <CheckboxEditor
                                                label="Please tick to confirm your client understands this may mean losing their full entitlement in the future and is happy to proceed."
                                                onChange={(v) => this.setState({ isConfirm: v })}
                                                value={this.state.isConfirm}
                                                validationRules={[
                                                    {
                                                        errorMessage:
                                                            "Please confirm that your client is happy to proceed with less than the maximum tax-free cash entitlement.",
                                                        validator: (v) => v == true,
                                                    },
                                                ]}
                                            />
                                        )}
                                    </>
                                ) : (
                                    MoneyDisplayHelper.getMoneyFormat(data?.CurrentPclsEntitlement)
                                )}

                                {!isPrimary && data?.CurrentPclsEntitlement?.Amount == crystallisationAmount && (
                                    <Alert
                                        alertType={AlertType.Error}
                                        message={
                                            "We are unable to pay the TFLS (Tax free lump sum) if it is equal to the crystallisation amount. Please contact the Admin team if you want to perform a Stand-alone Lump Sum."
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {showBenefitTable(data, lessThan75, hasEnhancedPensionProtection) && (
                    <>
                        <h3>Benefits in excess of LTA</h3>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Benefit</th>
                                    <th>Gross excess benefits</th>
                                    {showBenefitLastTwoColumn(data) && (
                                        <>
                                            <th>Tax payable on excess benefits</th>
                                            <th>Total net benefits</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Lump sum</td>
                                    <td>
                                        <PercentageTextBoxEditor
                                            cssWrapper="control-size-100"
                                            isNotForm={true}
                                            value={data?.GrossExcessBenefitLumpSum}
                                            alwaysValidateOnUpdate={true}
                                            disabled={data?.PensionOption == PensionOption.UFPLS}
                                            onChange={(v) => {
                                                onChange({
                                                    GrossExcessBenefitLumpSum: v,
                                                    ExcessBenefitsCalculations: {
                                                        ...data?.ExcessBenefitsCalculations,
                                                        LumpSumTaxPayable: {
                                                            Amount: calculateBeneiftsCharge(data, v, "lumpsum"),
                                                            Currency: CurrencyCode.GBP,
                                                        },
                                                        LumpSumTotalNetBenefits: {
                                                            Amount: calculateBeneiftsNet(data, v, "lumpsum"),
                                                            Currency: CurrencyCode.GBP,
                                                        },
                                                    },
                                                    GrossExcessBenefitTotal:
                                                        v + (data?.GrossExcessBenefitProvideAdditionalIncome || 0),
                                                });
                                            }}
                                            maxNumberOfDecimalPlaces={{
                                                errorMessage: "only allow two decimal places",
                                                value: 2,
                                            }}
                                            required={"Please enter"}
                                        />
                                    </td>
                                    {showBenefitLastTwoColumn(data) && (
                                        <>
                                            <td>
                                                {MoneyDisplayHelper.getMoneyFormat(
                                                    data?.ExcessBenefitsCalculations?.LumpSumTaxPayable
                                                )}
                                            </td>
                                            <td>
                                                {MoneyDisplayHelper.getMoneyFormat(
                                                    data?.ExcessBenefitsCalculations?.LumpSumTotalNetBenefits
                                                )}
                                            </td>
                                        </>
                                    )}
                                </tr>
                                {data?.PensionOption != PensionOption.UFPLS && (
                                    <tr>
                                        <td>Provide additional income</td>
                                        <td>
                                            <PercentageTextBoxEditor
                                                cssWrapper="control-size-100"
                                                isNotForm={true}
                                                value={data?.GrossExcessBenefitProvideAdditionalIncome}
                                                alwaysValidateOnUpdate={true}
                                                onChange={(v) => {
                                                    onChange({
                                                        GrossExcessBenefitProvideAdditionalIncome: v,
                                                        ExcessBenefitsCalculations: {
                                                            ...data?.ExcessBenefitsCalculations,
                                                            AdditionalIncomeTaxPayable: {
                                                                Amount: calculateBeneiftsCharge(data, v, "income"),
                                                                Currency: CurrencyCode.GBP,
                                                            },
                                                            AdditionalIncomeTotalNetBenefits: {
                                                                Amount: calculateBeneiftsNet(data, v, "income"),
                                                                Currency: CurrencyCode.GBP,
                                                            },
                                                        },
                                                        GrossExcessBenefitTotal:
                                                            v + (data?.GrossExcessBenefitLumpSum || 0),
                                                    });
                                                }}
                                                maxNumberOfDecimalPlaces={{
                                                    errorMessage: "only allow two decimal places",
                                                    value: 2,
                                                }}
                                                required={"Please enter"}
                                            />
                                        </td>
                                        {showBenefitLastTwoColumn(data) && (
                                            <>
                                                <td>
                                                    {MoneyDisplayHelper.getMoneyFormat(
                                                        data?.ExcessBenefitsCalculations?.AdditionalIncomeTaxPayable
                                                    )}
                                                </td>
                                                <td>
                                                    {MoneyDisplayHelper.getMoneyFormat(
                                                        data?.ExcessBenefitsCalculations
                                                            ?.AdditionalIncomeTotalNetBenefits
                                                    )}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td>Total</td>
                                    <td
                                        className={
                                            data?.GrossExcessBenefitTotal == 100 ? "text-positive" : ".text-negative"
                                        }
                                    >
                                        {data?.GrossExcessBenefitTotal?.toFixed(2)}%
                                    </td>
                                    {showBenefitLastTwoColumn(data) && (
                                        <>
                                            <td>
                                                {MoneyDisplayHelper.getMoneyFormat({
                                                    Amount:
                                                        (data?.ExcessBenefitsCalculations?.LumpSumTaxPayable?.Amount ||
                                                            0) +
                                                        (data?.ExcessBenefitsCalculations?.AdditionalIncomeTaxPayable
                                                            ?.Amount || 0),
                                                    Currency: CurrencyCode.GBP,
                                                })}
                                            </td>
                                            <td>
                                                {MoneyDisplayHelper.getMoneyFormat({
                                                    Amount:
                                                        (data?.ExcessBenefitsCalculations?.LumpSumTotalNetBenefits
                                                            ?.Amount || 0) +
                                                        (data?.ExcessBenefitsCalculations
                                                            ?.AdditionalIncomeTotalNetBenefits?.Amount || 0),
                                                    Currency: CurrencyCode.GBP,
                                                })}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}
                <hr className="mb-20" />
            </>
        );
    }
}
